import { CloudinaryImage } from '@cloudinary/url-gen';
import { format, quality } from '@cloudinary/url-gen/actions/delivery';
import { fill, scale } from '@cloudinary/url-gen/actions/resize';
import { auto as autoFormat } from '@cloudinary/url-gen/qualifiers/format';
import { auto as autoQuality } from '@cloudinary/url-gen/qualifiers/quality';

export interface CloudinaryImageOptions {
  /**
   * Target width in *device* pixels. Pass 2× the CSS size for crisp retina
   * rendering. With `height`, the image is cropped to fill the box (`c_fill`);
   * alone, it scales proportionally.
   */
  width?: number;
  /** Target height in device pixels. Pairs with `width` to crop-fill a box. */
  height?: number;
  /** Override automatic quality with a fixed 1–100 value. */
  quality?: number;
}

/**
 * Build a Cloudinary delivery image from a stored public ID, applying
 * auto-format (`f_auto`) and auto-quality (`q_auto`) so the CDN returns an
 * optimally-encoded, right-sized asset. Callers render with `.toURL()`.
 *
 * Storing the public ID rather than a baked `secure_url` keeps the cloud name,
 * CDN host, version, and format out of the database — see
 * packages/utils/src/image.ts in sidegig-web for the shared house pattern.
 */
export function getCloudinaryImage(
  cloudName: string,
  publicId: string,
  options?: CloudinaryImageOptions
): CloudinaryImage {
  let image = new CloudinaryImage(publicId, { cloudName }, { analytics: false })
    .delivery(quality(options?.quality ?? autoQuality()))
    .delivery(format(autoFormat()));

  const width = options?.width;
  const height = options?.height;
  if (width && height) image = image.resize(fill().width(width).height(height));
  else if (width) image = image.resize(scale().width(width));
  else if (height) image = image.resize(scale().height(height));

  return image;
}
